/**
 * 목록 화면 공용 컨트롤 클래스 — 화면마다 복제되던 클래스 문자열의 단일 출처.
 *
 * 왜 컴포넌트가 아니라 문자열 상수인가: 여기 있는 것들은 `<th>`·`<select>`처럼 **소비처가
 * 네이티브 엘리먼트를 직접 쥐고 있어야** 하는 자리다(테이블 시맨틱, 네이티브 select의 키보드
 * 동작). 껍데기 컴포넌트로 감싸면 `colSpan`·`onChange` 같은 속성을 통째로 다시 뚫어줘야 해서
 * 얻는 것 없이 층만 는다. 반대로 버튼처럼 **엘리먼트까지 소유해도 되는 것**은 이미 `Button`이
 * 가져갔다 — 그쪽을 쓸 수 있으면 그쪽을 쓸 것.
 *
 * 값의 출처는 목업 9종(`remote-files/safetyhub_*.html`)이고, 각 상수 주석에 대응 CSS 규칙과
 * 전사 과정에서 어긋난 값을 적어뒀다. **목업이 정본**이다.
 *
 * ⚠️ Tailwind는 클래스를 **정적 문자열로** 스캔하므로 각 상수는 완성형 리터럴로 둔다
 * (`text-${size}` 같은 동적 조합 금지). `apps/web`·`apps/admin` 두 tailwind.config.js가
 * `packages/ui/src/**`를 content에 포함하고 있어 이 파일의 클래스는 양쪽에서 생성된다
 * (확인 완료 — 빠지면 클래스만 붙고 CSS가 없어 조용히 무스타일이 된다).
 *
 * ⚠️ **도메인 이름을 넣지 말 것.** 여기 이름은 전부 `th`·pager·행 액션 같은 **구조 역할**이다.
 * `SITE_ROW_*`·`REPORT_TH_*`처럼 도메인 명사를 붙이면 같은 컨트롤이 화면 수만큼 늘어난다
 * (`Badge`의 tone 규약과 같은 기준: 도메인 → 시각 매핑은 소비처 책임).
 */

/**
 * 리스트 표 헤더 셀 `<th>` — 목업 공통 `th{}`
 * (`font-size:13px; font-weight:700; color:ink-500; letter-spacing:.04em; text-transform:uppercase;
 * padding:11px 16px; background:soft; border-bottom:1px solid line`).
 *
 * 목업 대비 미세 차이는 Tailwind 눈금에 스냅한 것: 13px→`text-xs`(12px), .04em→`tracking-wider`(.05em),
 * 11px→`py-3`(12px). 세 값 모두 임의 값(`text-[13px]` 등)으로 정확히 옮길 수 있지만, 승격 시점에
 * 이미 22개 셀이 이 값으로 렌더되고 있어 **여기서 바꾸면 승격이 곧 시각 변경**이 된다.
 * 정밀 정렬이 필요하면 별도 과제로 한 번에 할 것.
 *
 * **sticky는 일부러 넣지 않았다.** 표를 감싼 Card가 `overflow-x-auto`라 CSS 규격상 overflow-y도
 * auto로 계산돼, 스크롤 컨테이너가 되어버린 조상 안에서는 `position:sticky`가 붙어도 기준이 없어
 * 실질 무효다(=sticky가 붙을 대상이 없음). 헤더를 정말 붙이려면 셀의 `top`을 헤더+배너 오프셋
 * (Layout의 totalTopPx — 배너 4종 중 하나는 ResizeObserver로 높이가 바뀐다)과 표 3곳에서 각각
 * 동기화해야 해서 비용 대비 이득이 없다고 판단했다.
 *
 * 폭은 소비처가 붙인다 — `${TABLE_HEADER_CELL_CLASS} min-w-[280px]`, `... text-right` 등.
 */
export const TABLE_HEADER_CELL_CLASS =
  'border-b border-line bg-surface-soft px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-ink-500';

/**
 * 목록 상단 필터 컨트롤(네이티브 `<select>`) — 목업 9종 공통 `.filter-dd`
 * (`height:38px; padding:0 12px; border:1px solid line; border-radius:10px; background:#fff;
 * color:ink-700; font-size:14.5px; font-weight:600;` hover `background:hover`).
 *
 * ⚠️ **결재함의 필터와 혼동하지 말 것.** 결재함은 이것이 아니라 `.ff-row`/`.ff-field` 안의
 * `.ff-select`+`.ff-value`(15.5px/700/ink-900)를 쓴다 — 목업 9종 중 결재함에만 있는 별개
 * 컴포넌트라 값이 다른 게 정상이다. 그쪽은 `apps/web/src/pages/approvals/index.tsx`의
 * `FIELD_VALUE_SELECT_CLASS`로 **이름을 갈라 뒀다.** 둘을 같은 이름으로 합치려 하지 말 것.
 *
 * 목업 대비: radius 10px(`rounded-control`)인데 여기는 `rounded-card-sm`(8px), font-size는
 * 14.5px인데 `text-sm`(14px)이다. 둘 다 승격 전부터 렌더되던 값이라 그대로 옮겼다
 * (`Button`은 같은 `.btn` 계열을 `rounded-control`+`text-[14.5px]`로 이미 정렬했으므로,
 * 이 컨트롤도 같이 맞추는 건 **시각 변경을 동반하는 별도 과제**다).
 *
 * 폭은 일부러 뺐다 — 소비처가 `block w-full`·`w-44`·`w-40`으로 각각 정한다.
 * 포커스 링이 `focus:`(마우스 클릭에도 표시)인 것은 네이티브 select의 기존 동작을 유지한 것으로,
 * `focus-visible:`을 쓰는 아래 버튼형 상수들과 의도적으로 다르다.
 */
export const FILTER_SELECT_CLASS =
  'h-[38px] rounded-card-sm border border-line bg-white px-3 text-sm font-semibold text-ink-700 transition-colors hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-brand-500';

/**
 * 표 하단 페이지네이션 버튼 — 목업 9종 공통 `.pager .nav button`
 * (`min-width:32px; height:32px; padding:0 8px; border-radius:7px; border:1px solid line;
 * background:#fff; color:ink-600; font-size:14px; font-weight:600;` hover `background:hover;
 * color:ink-900`, disabled `opacity:.4; cursor:not-allowed`).
 *
 * 목업 대비: radius 7px→`rounded-card-sm`(8px), padding 0 8px→`px-3`(12px), `min-width:32px` 없음.
 * 앞의 둘은 1~4px 차이라 그대로 뒀고, `min-w`는 소비처가 필요할 때 붙인다.
 *
 * ⚠️ 목업의 현재 페이지 상태(`.pager .nav button.cur{background:brand-600;color:#fff;
 * border-color:transparent}`)는 여기 없다. `approvals/index.tsx`의 페이지 번호 버튼이 그 상태를
 * **인라인으로** 들고 있고, 비활성 분기는 이 상수와 색이 완전히 같다 — 같은 목업 규칙이 두 벌로
 * 갈려 있다는 뜻이다. 통합하려면 번호 버튼의 `min-w-[32px] px-2`와 이 상수의 `px-3` 중 하나를
 * 골라야 해서(=시각 변경) 승격 범위 밖으로 두고 기록만 남긴다.
 */
export const PAGER_BUTTON_CLASS =
  'h-8 rounded-card-sm border border-line bg-white px-3 text-sm font-semibold text-ink-600 transition-colors hover:bg-surface-hover hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40';

/**
 * 캘린더 월/주 이동 화살표 — 목업 `.cal-arrow`
 * (`width:34px; height:34px; border-radius:9px; border:1px solid line; background:#fff;
 * display:grid; place-items:center; color:ink-600;` hover `background:hover; color:ink-900`).
 * 목업 값을 그대로 옮긴 것이라 어긋난 값이 없다.
 *
 * 목업 9종 중 스케쥴에만 있는 규칙이고 현재 사용처도 스케쥴 한 곳이다. 그래도 여기 두는 이유는
 * 형제 컨트롤(pager·행 액션)이 전부 올라오는데 이것만 페이지에 남으면 "어떤 건 공용이고 어떤 건
 * 아닌가"의 기준이 사용 횟수로 흐려지기 때문이다 — 기준은 **구조 역할이 페이지 고유인가**이고,
 * 34px 정사각 아이콘 이동 버튼은 고유하지 않다.
 */
export const CALENDAR_NAV_BUTTON_CLASS =
  'grid h-[34px] w-[34px] flex-none place-items-center rounded-[9px] border border-line bg-white text-ink-600 transition-colors hover:bg-surface-hover hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';

/**
 * 표 행 끝 액션(⋯ 메뉴 등) 트리거 — 목업 9종 공통 `.row-actions .ico`
 * (`width:30px; height:30px; border-radius:8px; display:grid; place-items:center; color:ink-400;
 * background:transparent; border:1px solid transparent;` hover `background:#fff; border-color:line;
 * color:ink-700; box-shadow:0 2px 6px rgba(15,23,42,.08)`).
 *
 * hover 그림자를 `shadow-card-sm`(0 1px 2px/.04)에서 **`shadow-control-hover`(0 2px 6px/.08)로
 * 정정**했다. 후자는 `tailwind-preset.js`가 *바로 이 규칙*(`.row-actions .ico:hover`)을 근거로
 * 추가한 토큰인데(주석에 명시) 이 상수만 옛 값을 쓰고 있었다 — 의도적 차이가 아니라 토큰 도입
 * 전에 쓰인 값이 남은 드리프트라 목업 값으로 맞춘다.
 *
 * ⚠️ **`Button`의 `ghost` variant와 같은 목업 규칙에서 나왔다.** ghost는
 * `border-transparent bg-transparent text-ink-400 hover:border-line hover:bg-white
 * hover:text-ink-700 hover:shadow-control-hover`로 색이 이제 이것과 완전히 같고, 남은 차이는
 * 치수뿐이다(여기 30px/radius 8px vs `size="sm"`+`iconOnly` 32px/radius 10px — 후자는 목업의
 * 30/32px 불일치를 8px 그리드로 스냅한 결과). 즉 이 상수는 **`buttonClassName('ghost','sm','',true)`로
 * 흡수되는 게 최종 모습**이고, 그 이관은 +2px/+2px 시각 변경이라 별도 판단이 필요해 남겨둔다.
 * 새 행 액션을 만든다면 이 상수보다 `Button`의 ghost+iconOnly를 먼저 검토할 것.
 */
export const ROW_ACTION_TRIGGER_CLASS =
  'grid h-[30px] w-[30px] place-items-center rounded-card-sm border border-transparent text-ink-400 transition-colors hover:border-line hover:bg-white hover:text-ink-700 hover:shadow-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';

/**
 * 카드 안 섹션 제목(`<h2>`/`<h3>`) — 시안 카드 헤더 결.
 * 값은 대시보드가 로컬 상수(`DashboardDesktop.tsx`의 `SECTION_TITLE`)로 들고 있던 것과 **같다** —
 * 설정·프로필류가 쓰던 `text-lg font-medium`(18px/500)과 갈려 있어서, 더 촘촘한 시안 쪽으로 모은다.
 *
 * 여백은 소비처가 붙인다(`${CARD_SECTION_TITLE_CLASS} mb-4`) — 카드 헤더띠 안이냐 본문 위냐에 따라
 * 필요한 마진이 달라서 상수에 넣으면 절반은 상쇄 클래스를 덧붙이게 된다.
 *
 * ⚠️ 위험 구역 제목처럼 **색만 다른 변형은 이 상수를 문자열 치환하지 말 것**(Tailwind 정적 스캔이
 * 깨진다). `text-[15px] font-extrabold tracking-tight text-rose-800`처럼 완성형 리터럴로 쓴다.
 */
export const CARD_SECTION_TITLE_CLASS = 'text-[15px] font-extrabold tracking-tight text-ink-900';

/**
 * 관리 화면 폼 라벨 — rc 작성폼의 라벨 규약(13px/700)을 설정·프로필류에 준용한 것.
 * 아래 `FORM_CONTROL_CLASS`와 한 쌍으로 쓴다(`mb-1.5`가 여기 들어 있는 이유도 그 쌍 간격이라서다).
 */
export const FORM_LABEL_CLASS = 'block text-[13px] font-bold text-ink-700 mb-1.5';

/**
 * 관리 화면 폼 컨트롤(`<input>`/`<select>` 공통) — 시안 input 결
 * (`height:42px; border-radius:10px; border:1px solid line; font-size:14px; font-weight:600;`
 * focus `border-color:brand-500; box-shadow:0 0 0 3px brand-100`).
 *
 * 옛 결(`border-ink-300 rounded-control shadow-sm focus:ring-brand-500`)과 갈리는 지점은 셋:
 * 테두리색을 `line` 토큰으로, 상시 그림자를 제거(시안 컨트롤은 평면), 포커스를 링 단독이 아니라
 * **테두리+연한 3px 링**으로. 마지막 것이 시안에서 가장 눈에 띄는 차이다.
 *
 * ⚠️ **체크박스·라디오·토글에는 쓰지 말 것** — `h-[42px] block w-full`이 네이티브 체크박스를
 * 통째로 늘려버린다. 그쪽은 각자의 결을 유지한다.
 *
 * `placeholder:font-medium`은 값(600)보다 placeholder를 가볍게 둬서 입력 유무가 굵기로도
 * 읽히게 한 것이다.
 */
export const FORM_CONTROL_CLASS =
  'block w-full h-[42px] rounded-[10px] border border-line bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-100 disabled:bg-ink-50 disabled:text-ink-500';

/**
 * `<textarea>` 변형 — 위 컨트롤에서 고정 높이만 최소 높이+세로 패딩으로 바꾼 것.
 * (`FORM_CONTROL_CLASS.replace(...)`로 파생시키지 않는다: Tailwind는 소스를 **텍스트로** 스캔해서
 * 런타임 문자열 조작의 결과 클래스를 보지 못할 수 있다. 값이 중복돼도 완성형 리터럴이 정본이다.)
 */
export const FORM_TEXTAREA_CLASS =
  'block w-full min-h-[96px] py-2.5 rounded-[10px] border border-line bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-100 disabled:bg-ink-50 disabled:text-ink-500';
